import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIconModule, MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { AppUpdateService } from '@core/app-update/app-update.service';
import { ApplicationStore } from '@core/store/application.store';
import { AuthService } from '@features/auth/auth.service';
import { TranslocoService } from '@jsverse/transloco';
import { filter, map, switchMap, tap } from 'rxjs';
import { LayoutComponent } from './core/layout/layout.component';

@Component({
  selector: 'fc-root',
  imports: [LayoutComponent, MatIconModule],
  providers: [AppUpdateService],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './app.component.html',
})
export class AppComponent {
  private readonly applicationStore = inject(ApplicationStore);
  private readonly authService = inject(AuthService);
  private readonly appUpdateService = inject(AppUpdateService);
  private readonly matIconRegistry = inject(MatIconRegistry);
  private readonly domSanitizer = inject(DomSanitizer);
  private readonly router = inject(Router);
  private readonly translocoService = inject(TranslocoService);

  constructor() {
    this.applicationStore.init();
    this.authService.getProfile().subscribe();

    this.applicationStore.updateLanguage(this.applicationStore.getActiveLanguage());

    this.appUpdateService.init();
    this.appUpdateService.checkForUpdates();

    this.updateTitle();

    this.matIconRegistry.setDefaultFontSetClass('material-symbols-outlined');

    this.addIcons();
  }

  private updateTitle(): void {
    this.router.events
      .pipe(
        takeUntilDestroyed(),
        filter((event) => event instanceof NavigationEnd),
        map(() => {
          let route: ActivatedRoute = this.router.routerState.root;
          let routeTitle = '';
          while (route!.firstChild) {
            route = route.firstChild;
          }
          if (route.snapshot.data['title']) {
            routeTitle = route!.snapshot.data['title'];
          }
          return routeTitle;
        }),
        filter((routeTitle: string) => !!routeTitle),
        switchMap((routeTitle: string) => {
          const scope = routeTitle.split('.')[0];
          const title = routeTitle.split('.').slice(1).join('.') || '';
          return this.translocoService.selectTranslate(title, {}, scope).pipe(
            tap((pageName: string) => {
              this.applicationStore.updatePageName(pageName);
            }),
          );
        }),
      )
      .subscribe();
  }

  private addIcons(): void {
    this.matIconRegistry.addSvgIcon('menu-home', this.sanitizeImage('/images/icons/menu-home.svg'));
    this.matIconRegistry.addSvgIcon('menu-contracts', this.sanitizeImage('/images/icons/menu-contracts.svg'));
    this.matIconRegistry.addSvgIcon('menu-invoices', this.sanitizeImage('/images/icons/menu-bills.svg'));
    this.matIconRegistry.addSvgIcon('menu-settings', this.sanitizeImage('/images/icons/menu-settings.svg'));
    this.matIconRegistry.addSvgIcon('menu-logout', this.sanitizeImage('/images/icons/menu-logout.svg'));
    this.matIconRegistry.addSvgIcon('menu-contact', this.sanitizeImage('/images/icons/menu-contact.svg'));
    this.matIconRegistry.addSvgIcon('user', this.sanitizeImage('/images/icons/user.svg'));
    this.matIconRegistry.addSvgIcon('online-pay', this.sanitizeImage('/images/icons/online-pay.svg'));
    this.matIconRegistry.addSvgIcon('bill-open', this.sanitizeImage('/images/icons/facture-pause.svg'));
    this.matIconRegistry.addSvgIcon('bill-closed', this.sanitizeImage('/images/icons/facture-play.svg'));
    this.matIconRegistry.addSvgIcon('whatsapp', this.sanitizeImage('/images/whatsapp.svg'));
  }

  private sanitizeImage(icon: string): SafeResourceUrl {
    return this.domSanitizer.bypassSecurityTrustResourceUrl(icon);
  }
}
